/**
 * Tenant Service
 *
 * Handles multi-tenant domain detection and branding configuration.
 * Responsible for fetching photographer configuration based on custom domains
 * and applying tenant-specific branding to the application.
 */

import api from './api';
import type { ApiResponse, ApiError } from '@/types';

/**
 * Branding configuration for tenant customization
 */
export interface TenantBranding {
  primaryColor: string;
  logo?: string;
  businessName?: string;
}

/**
 * Tenant settings for gallery configuration
 */
export interface TenantSettings {
  watermarkEnabled: boolean;
  allowDownloads: boolean;
  currency: string;
}

/**
 * Complete tenant configuration response from backend
 */
export interface TenantConfig {
  photographerId: string;
  photographerName: string;
  domain: string;
  branding: TenantBranding;
  settings: TenantSettings;
}

/**
 * Error thrown when tenant configuration cannot be loaded
 */
export class TenantConfigError extends Error {
  constructor(message: string, public readonly domain: string) {
    super(message);
    this.name = 'TenantConfigError';
  }
}

/**
 * TenantService class for domain detection and branding management
 */
class TenantService {
  private cachedConfig: TenantConfig | null = null;
  private currentDomain: string | null = null;

  // Platform domains that should show landing page instead of gallery
  private readonly PLATFORM_DOMAINS = [
    'pixel-share.com',
    'www.pixel-share.com',
    'localhost',
    'localhost:5173',
  ];

  /**
   * Get the current domain from window.location
   *
   * @returns Current hostname (e.g., 'photos.n3rd-media.com', 'localhost')
   */
  getCurrentDomain(): string {
    if (typeof window === 'undefined') {
      return '';
    }
    return window.location.host;
  }

  /**
   * Check if current domain is a platform domain (not a customer domain)
   *
   * @param domain - Domain to check (uses current domain if not provided)
   * @returns True if this is a platform domain, false if customer domain
   */
  isPlatformDomain(domain?: string): boolean {
    const targetDomain = domain || this.getCurrentDomain();
    return this.PLATFORM_DOMAINS.some(platformDomain =>
      targetDomain.toLowerCase().includes(platformDomain.toLowerCase())
    );
  }

  /**
   * Fetch tenant configuration from backend based on domain
   *
   * @param domain - Optional domain override (uses current domain if not provided)
   * @returns Promise resolving to tenant configuration
   * @throws TenantConfigError if configuration cannot be loaded
   */
  async getTenantConfig(domain?: string): Promise<TenantConfig> {
    const targetDomain = domain || this.getCurrentDomain();

    // If this is a platform domain, return null (will show landing page)
    if (this.isPlatformDomain(targetDomain)) {
      throw new TenantConfigError(
        'Platform domain - showing landing page',
        targetDomain
      );
    }

    // Return cached config if domain hasn't changed
    if (this.cachedConfig && this.currentDomain === targetDomain) {
      return this.cachedConfig;
    }

    try {
      // Fetch configuration from backend
      // Backend returns: { success: true, data: {...}, message: "..." }
      const response = await api.get<ApiResponse<TenantConfig>>('/gallery/config', {
        params: { domain: targetDomain },
      });

      // Extract data from nested response structure
      const config = response.data.data;

      // Cache the configuration
      this.cachedConfig = config;
      this.currentDomain = targetDomain;

      return config;
    } catch (error) {
      // Handle API errors
      const apiError = error as ApiError;
      const errorMessage =
        apiError?.message ||
        'Failed to load tenant configuration';

      throw new TenantConfigError(errorMessage, targetDomain);
    }
  }

  /**
   * Apply tenant branding to the DOM
   * Sets document title, CSS custom properties, and favicon
   *
   * @param config - Tenant configuration with branding settings
   */
  applyBranding(config: TenantConfig): void {
    const { branding } = config;

    try {
      // Set document title to business name
      if (branding.businessName) {
        document.title = branding.businessName;
      }

      // Set CSS variable for primary color
      if (branding.primaryColor) {
        document.documentElement.style.setProperty(
          '--color-primary',
          branding.primaryColor
        );
      }

      // Update favicon if logo exists
      if (branding.logo) {
        this.updateFavicon(branding.logo);
      }
    } catch {
      // Don't throw - branding application is non-critical
    }
  }

  /**
   * Update the favicon link element
   *
   * @param logoUrl - URL of the logo/favicon image
   */
  private updateFavicon(logoUrl: string): void {
    // Find existing favicon link or create new one
    let favicon = document.querySelector<HTMLLinkElement>(
      'link[rel="icon"]'
    );

    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }

    favicon.href = logoUrl;
  }

  /**
   * Clear cached configuration
   * Useful when switching tenants or forcing a refresh
   */
  clearCache(): void {
    this.cachedConfig = null;
    this.currentDomain = null;
  }

  /**
   * Check if a tenant configuration is currently loaded
   *
   * @returns True if configuration is cached
   */
  hasConfig(): boolean {
    return this.cachedConfig !== null;
  }

  /**
   * Get the currently cached configuration without fetching
   *
   * @returns Cached configuration or null
   */
  getCachedConfig(): TenantConfig | null {
    return this.cachedConfig;
  }
}

// Export singleton instance
export const tenantService = new TenantService();

// Export class for testing/custom instances
export default TenantService;
